import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import { ApplicableTableSettings } from "../../tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import { myMemoTable } from "./tableSettings";
import { TaskReminder, TaskReminderList } from '../../fms.core/TaskReminder';
import ApplicationContext from '../../ApplicationContext';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { DateTimeRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer, TextRenderer } from '../../uiControls/SmartTableRenderers';
import IfmsModal from '../../ui/IfmsModal';
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import LeftRightSplit from '../../ui/LeftRightSplit';
import MultilineTextBox from '../../ui/MultilineTextBox';
import { ListResult } from '../../framework.core/data/ListResult';
import Number from '../../ui/Number';
import PrimaryInverseButton from '../../ui/PrimaryInverseButton';
import { compareIso, now, nowDate } from '../../dateUtils';
import _ from 'lodash';
import useDirtyCheck from '../../hooks/useDirtyCheck2';
import { SQLField, SQLInfo, SQLType } from '../../framework.core/data/SQLInfo';
import { ApiResult, InsertResultsWithData } from '../../framework.core/data/ApiResult';
import { JobNames } from '../../framework.core/JobNames';
import useTableNavigation from '../../ui/tables/useTableNavigation';
import Select from '../../ui/Select';

interface MyMemoStorageKeys {
  modelStorage: string,
};

interface MyMemoParams {
  onClickEdit?: (tbName:string, tbId:number, acctTbName: string) => void,
  showMainPop: boolean,
  setShowMainPop: (x: boolean)=> void,
  mainTop: string,
  mainLeft: string,
};

interface MyMemoLoadedParams {
  taskReminder: TaskReminder[],
  onClickEdit?: (tbName:string, tbId:number, acctTbName: string) => void,
  showMainPop: boolean,
  setShowMainPop: (x: boolean)=> void,
  reload : ( y?: string) =>  Promise<void>
  setInStorage: (x: Partial<TaskReminderList>) => void,
  updateMContent: (index: number) => void,
  loading:boolean,
  mainTop: string,
  mainLeft: string,
};

interface EditRendererParam {
  data: TaskReminder[],
  row: any,
  cell: any,
}

function getMyMemoStorageKeys(): MyMemoStorageKeys {
  return { modelStorage: 'reminder-memo-view' }
}

function MyMemoImpl(params: MyMemoParams) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const { modelStorage } = getMyMemoStorageKeys();
  const { setShowMainPop, showMainPop, mainTop, mainLeft } = params;

  const [inStorage, setInStorage, , ] = useTypedFastStorage<TaskReminderList>(modelStorage, null);
  const {setCleanState} = useDirtyCheck(modelStorage, ['remindDate']);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    initialize();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  async function loadExisting() {
    const apiResult: ListResult | null = await appContext.api.getTaskReminder(true, appContext.user ? appContext.user.id ?? '' : '');
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      return [];
    }
    return apiResult?.entities ?? [];
  }

  async function initialize() {
    setLoading(true);
    const data = await loadExisting();
    setCleanState(data);
    setInStorage({taskReminderList : data});
    setLoading(false);
  }

  async function reload(action: string = '') {
    setCleanState(_.cloneDeep(inStorage ?? []));
    const apiResult: ListResult | null = await appContext.api.getTaskReminder(true, appContext.user ? appContext.user.id ?? '' : '');
    if (!apiResult || !apiResult.success) {
      return;
    }
    const data: TaskReminder[] = apiResult?.entities;
    setCleanState(data);

    setInStorage({taskReminderList : _.cloneDeep(data)});
    //Done 처리 된 상태에서 Reminder Task가 하나도 없을때 My Task의 icon 사라지도록 처리 (화면은 유지)
    if(action === 'done'){
      if (data.length === 0) {
        //appContext.setUseCheckReminder(!appContext.useCheckReminder);
      }
    }
  }

  async function updateMContent(index: number){
      const userId = appContext.user ? appContext.user.id ?? '' : '';
      let sqlInfos: SQLInfo[] = [];
      sqlInfos.push (new SQLInfo({
        tableName: "T_MEMO",
        type: SQLType.Update,
        fields: [
          new SQLField({fieldName: "f_u2id", propertyName: 'ModifiedBy', value: userId}),
          new SQLField({fieldName: 'f_u2date', propertyName: 'ModifiedAt', value: 'GetDate()'}),
          new SQLField({fieldName: "f_contents", propertyName: 'Description', value: inStorage?.taskReminderList[index].mContents})
        ],
        key: new SQLField({fieldName: "f_id", propertyName: 'Id', value: inStorage?.taskReminderList[index].mId})
      }))
      const apiResult: ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(JobNames.Memo, sqlInfos, {});
      if(!apiResult.success){
        await showGlobalOkDialog("Failed to update");
      } else {
        await showGlobalOkDialog("Successfully Updated.");
      }
  }

  

  if (!inStorage) {
    return null;
  }

  return (
    <MyMemoInitializedImpl
      taskReminder={inStorage.taskReminderList}
      onClickEdit={params.onClickEdit}
      showMainPop={showMainPop}
      reload = {reload}
      setShowMainPop={setShowMainPop}
      setInStorage={setInStorage}
      updateMContent={updateMContent}
      loading={loading}
      mainTop={mainTop}
      mainLeft={mainLeft}
    />
  );
}

function MyMemoInitializedImpl({
  taskReminder,
  onClickEdit,
  showMainPop,
  setShowMainPop,
  reload,
  setInStorage,
  updateMContent,
  loading,
  mainTop,
  mainLeft,
}: MyMemoLoadedParams) {
  const tableId = 'MyMemoTable';

  const appContext = useContext(ApplicationContext);
  const [selectedRow, setSelectedRow] = useState(0);
  const [snooze, setSnooze] = useState(false);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const modalPosition = useRef<{ top: number; left: number }>({ top: 600, left: 1100 });     //default 위치 설정
  const snoozePosition = useRef<{top: string, left: string}>({top: '600px', left:'1300px'}); //default 위치 설정

  const { getSelectedRow, selectTableRow, selectTableCol, tableNavigationCleanup } = useTableNavigation(tableId, onRowSelect, null, [], true, false, false, false, undefined, false, true);
  const [enterTime, setEnterTime] = useState<string>('10m');
  useEffect(()=>{
      if(taskReminder && taskReminder.length > 0 && !loading){
        setTimeout(() => {
          selectTableRow(0);
          onRowSelect(0);
          selectTableCol(5); //subject column에 focus
        }, 50);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },[loading]);


  useEffect(() => {
    selectTableRow(getSelectedRow() ?? 0);
       // eslint-disable-next-line react-hooks/exhaustive-deps
  },[getSelectedRow()]);


  const handlePositionChange = useCallback((position) => {
    if(position.top && position.left){
        modalPosition.current.top = position.top;
        modalPosition.current.left = position.left;
        snoozePositionChange(position.top,position.left);
    }

  }, []);

  function snoozePositionChange(x: number, y: number){
    let left = (y + 200) + "px";
    let top = x+"px";
    snoozePosition.current.top = top;
    snoozePosition.current.left = left;
  }


  function onRowSelect(rowIndex: number) {
    setSelectedRow(!isNaN(rowIndex) ? rowIndex : -1);
  }

  function changeLineItem(change: Partial<TaskReminder>, index: number){
    taskReminder[index] = _.merge(taskReminder[index], change);
    setInStorage({taskReminderList: taskReminder});
  }

  const tableOptions: ApplicableTableSettings = {
    ...myMemoTable,
    height: '160px',
    tableName: tableId,
    selectable: true,
    rowProps: (row: any) => { 
      if(compareIso(now(), taskReminder[row.index]?.dueDate??'') >= 0){
        return {
          style: { backgroundColor: '#FFC0CB',},
          onClick: () => onRowSelect(row.index) 
        };
      }

      return { onClick: () => onRowSelect(row.index) }
    },
    data: taskReminder.map(x => ({ ...x })),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      {accessor: 'doneParam', Cell: DoneRenderer},
      //{ accessor: 'editParam', Cell: EditRenderer },
      { accessor: 'mTitle', Cell: TextAlignLeftRenderer },
      //{ accessor: 'remindDate', Cell: DateTimeRenderer },
      { accessor: 'dueDate', Cell: DateTimeRenderer },
      { accessor: 'opType', Cell: OpTypeRenderer },
      { accessor: 'refNo', Cell: TextAlignLeftRenderer },
      { accessor: 'mU1ID', Cell: TextAlignLeftRenderer },
      { accessor: 'mU1Date', Cell: DateTimeRenderer },
    ],
  };

  function EditRenderer({ data, row, cell }: EditRendererParam) {
    return <PrimaryInverseButton onClick={() => {
      if (onClickEdit)
        onClickEdit(data[row.index].mTbName, data[row.index].mTbId, data[row.index].acctTbName);
      appContext.setAppInfoAreaContent('memo');
      appContext.setMyMemoTaskReminder(true);
      appContext.setMyMemoId(data[row.index].mId??0);
      setShowMainPop(false);
    }} style={{ width: '100%', color: 'blue', fontWeight: 'bold', fontSize: '10px', padding: '0 !important' }}>Edit</PrimaryInverseButton>
  }

  //Memo Detail로 이동하지 않고 Done 버튼 클릭 시 바로 Done이 되도록 처리
  function DoneRenderer({ data, row, cell }: EditRendererParam) {
    return <PrimaryInverseButton onClick={async () => {
      const userId = appContext.user ? appContext.user.id ?? '' : '';
      let sqlInfos: SQLInfo[] = [];
      sqlInfos.push (new SQLInfo({
        tableName: "T_MEMO",
        type: SQLType.Update,
        fields: [
          new SQLField({fieldName: "f_u2id", propertyName: 'ModifiedBy', value: userId}),
          new SQLField({fieldName: 'f_u2date', propertyName: 'ModifiedAt', value: 'GetDate()'})
        ],
        key: new SQLField({fieldName: "f_id", propertyName: 'Id', value: data[row.index].mId})
      }))
      sqlInfos.push( new SQLInfo({
                tableName: "T_AOTHERINFO",
                type: SQLType.Update,
                fields: [
                  new SQLField({ fieldName: "F_N2", propertyName: "N2", value: 0 }),
                ],
                whereFields: [
                  new SQLField({ fieldName: 'f_tbname', propertyName: 'TbName', value: 'T_MEMO' }),
                  new SQLField({ fieldName: 'f_tbid', propertyName: 'TbId', value: data[row.index].mId }),
                  new SQLField({ fieldName: 'f_type', propertyName: 'Type', value: 0 })
                ]
        }));

      const apiResult: ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(JobNames.Memo, sqlInfos, {});
      if(!apiResult.success){
        await showGlobalOkDialog("Failed to change status to 'Done'");
      } else {
        //데이터 갱신
        await reload("done");
        selectTableRow(0);
        onRowSelect(0);
      }
    }} style={{ width: '100%', color: 'blue', fontWeight: 'bold', fontSize: '10px', padding: '0 !important' }}>Done</PrimaryInverseButton>
  }

  //My Task > Task List와 동일하게 opType 변경
  function OpTypeRenderer({ data, row, cell }: EditRendererParam){
    const l = data[row.index];
    let opType = getOpType(l.opType, l.acctTbName);
      return (
       <div className="ifms-table-default">{opType}</div>
      );

  }

  function getOpType(opType: string, acctTbName: string){
    switch(opType){
      case "OOM": return "OEM";
      case "OOH": return "OEH";
      case "AOM": return "AEM";
      case "AOH": return "AEH";
      case "GEN": return "OTH";
      case "WRE": return "WHR";
      case "WSH": return "WHS";
      case "PAY": return "Payroll";
      case "INV": 
        if(acctTbName === "") return "G.INV";
        else return "INV";
      case "APH": 
        if(acctTbName === "") return "G.BIL";
        else return "BIL";
      case "CRD": return "CrDb";
      case "DEP":
        if(acctTbName.substring(0,1) === 'D' && acctTbName.substring(2) === '1') return "Agent. S";
        else if (acctTbName.substring(0,1) === 'D' && acctTbName.substring(2) !== '1') return "C. Pay";
        else if (acctTbName.substring(0,1) === 'M') return "CC Pay";
        else return "B. Pay";
      case "ner":
        return "General";
      default: return opType;
    }
  }


  function close() {
    tableNavigationCleanup();
    setShowMainPop(false);
    appContext.setMyMemoTaskReminder(false);
    appContext.setAppInfoAreaContent(appContext.appInfoAreaContent);
  }

  const timeItems =[
    {label: '10 Minutes', value:'10m' },
   // {label: '30 Minutes', value:'30m' },
   // {label: '1 Hour', value: '1h' },
   // {label: '2 Hours', value: '2h' },
    {label: '1 Day', value: '1d' },
    {label: '2 Days', value: '2d' },
  ]


  return (
    <IfmsModal
      top={mainTop}
      left={mainLeft}
      isSmallHeader
      isBlueHeader
      cancelOnEsc
      title={'Task Reminder List'}
      isOpen={showMainPop}
      onCancel={() => { close()}}
      draggable
      width={`${770}px`}
      onPositionChange={handlePositionChange}
      >
      {
        <div id={"myMemo-popup"} className='report-popup' style={{ marginBottom: '-20px' }}>
          <div style={{ width: '170px', marginBottom: '10px' }}>
            <InlineInputControlRow
              smallMargin
              mode="w60"
              label="Assign To : "
            >
              <TextBox value={appContext.user ? appContext.user.id ?? '' : ''} disabled />
            </InlineInputControlRow>
          </div>
          <div>
            <SmartTable {...tableOptions} />
          </div>
          {/* <div style={{ display: 'flex' }}>
            <LeftRightSplit
              leftSize="calc(100% - 300px)"
              rightSize="300px"
              left={
                <TextBox value={taskReminder[selectedRow]?.mTitle ?? ''} disabled />
              }
              right={
                <div style={{ paddingLeft: '50px' }}>
                  <TextBox value={taskReminder[selectedRow]?.refNo ?? ''} disabled />
                </div>
              }
            />
          </div>
          <div className="clearfix" />
          <div style={{ marginTop: '3px' }}>
            <MultilineTextBox
              maxLength={200}
              style={{ height: '150px' }}
              value={taskReminder[selectedRow]?.mContents ?? ''}
              onChange={(e) => { changeLineItem({mContents: e.target.value}, selectedRow)}} />
          </div> */}
          <div className='report-footer-2buttons' style={{marginTop:'10px'}}>
            {/* <SecondaryButton
              focusable={true}
              size="regular"
              style={{ width: '150px' }}
              onClick={async () => {
                setshowAll(!showAll);
                await reload(!showAll);
                setSelectedRow(0);
               }}>
                {showAll ? 'Show Over Due Only' : 'Show All'}
            </SecondaryButton> */}
            {/* <SecondaryButton
              focusable={true}
              size="regular"
              style={{ width: '90px' }}
              onClick={() => { setSnooze(true) }}>
              Snooze              
            </SecondaryButton> */}
             {/* <SecondaryButton
              focusable={true}
              size="regular"
              style={{ width: '90px' }}
              onClick={async () => {updateMContent(selectedRow)}}>
              Update
            </SecondaryButton>  */}
            <SecondaryButton
              focusable={true}
              size="regular"
              style={{ width: '90px' }}
              onClick={() => {close()}}>
              Close
            </SecondaryButton>
          </div>
        </div>
      }
      {snooze &&
        <IfmsModal
          top={snoozePosition.current.top}
          left={snoozePosition.current.left}
          isSmallHeader
          isBlueHeader
          cancelOnEsc
          onCancel={() => { setSnooze(false) }}
          draggable
          isOpen={true}
          width={`${300}px`}
          title={'Snooze Reminder'}>
          {
            <div className='report-popup' style={{ marginBottom: '-20px' }}>
              <div style={{ width: '210px' }}>
                <InlineInputControlRow
                  smallMargin
                  mode="smaller"
                  label="Enter Times : "
                >
                  <Select
                    value={enterTime}
                    items={timeItems}
                    onChange={(e) => {setEnterTime(e.target.value)}}
                  />
                </InlineInputControlRow>
              </div>
              <div className='report-footer-2buttons' style={{marginTop:'10px'}}>
                <SecondaryButton
                  focusable={true}
                  size="regular"
                  style={{ width: '60px' }}
                  onClick={async () => {
                    //const apiResult = 
                    await appContext.api.saveMinutes(enterTime, appContext.user ? appContext.user.id ?? '' : '', taskReminder[selectedRow]?.mId);
                    setEnterTime('10m');
                    setSnooze(false);
                    await reload('done');
                    //setShowMainPop(false);
                    //appContext.setMyMemoTaskReminder(false);
                    //appContext.setUseCheckReminder(!appContext.useCheckReminder);
                  }}>
                  OK
                </SecondaryButton>
                <SecondaryButton
                  focusable={true}
                  size="regular"
                  style={{ width: '60px' }}
                  onClick={() => { setSnooze(false) }}>
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          }
        </IfmsModal>
      }
    </IfmsModal>

  );
}

const MyMemoDetails = ({
  onClickEdit,
  setShowMainPop,
  showMainPop,
  mainTop, 
  mainLeft,
}: { onClickEdit?: (tbName:string, tbId:number, acctTbName: string) => void,  setShowMainPop:(x: boolean)=> void, showMainPop: boolean, mainTop: string, mainLeft: string }) => {
  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <MyMemoImpl setShowMainPop={setShowMainPop} showMainPop={showMainPop} onClickEdit={onClickEdit} key={`MyMemo-details`} mainTop={mainTop} mainLeft={mainLeft} />
  );
};
export default MyMemoDetails;
